import { useParams, useNavigate, Link } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useAuth } from "@/lib/auth";
import AuthLayout from "@/components/layout/AuthLayout";
import { toast } from "sonner";
import { usePostResetPassword, useCheckResetToken } from "@/hooks/useAuth";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { FormInput } from "@/components/forms/FormControls";
import { resetPasswordSchema, type ResetPasswordValues } from "@/schemas/auth";
import { PUBLIC_ROUTES } from "@/routes/url";

export default function ResetPassword() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { installSession } = useAuth() || {};
  const { mutateAsync, isPending: busy } = usePostResetPassword();
  const form = useForm<ResetPasswordValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { password: "", confirmPassword: "" },
  });
  const { data: tokenData, isLoading: validating, error: tokenError } = useCheckResetToken(token || "");
  const email = tokenData?.email || "";
  const tokenErr = tokenError ? ((tokenError as Error).message || "This reset link isn't valid.") : null;

  const submit = async ({ password }: ResetPasswordValues) => {
    if (!token || !installSession) return;
    try {
      const { data, message } = await mutateAsync({ token, password });
      if (!data) throw new Error(message);
      await installSession(data.token, data.user);
      toast.success("Password updated");
      navigate(PUBLIC_ROUTES.OVERVIEW, { replace: true });
    } catch {
      // The mutation hook displays error.message.
    }
  };

  // Mirrors the form below so the card does not resize once the token checks
  // out. CenteredCardSkeleton is not reused here: it brings its own
  // min-h-screen wrapper, which would nest inside AuthLayout.
  if (validating) return (
    <AuthLayout>
      <div role="status" aria-label="Checking your reset link" data-testid="reset-validating">
        <Skeleton className="h-3 w-28" />
        <Skeleton className="mt-4 h-9 w-3/5" />
        <Skeleton className="mt-5 h-4 w-4/5" />
        <div className="mt-8 flex flex-col gap-4">
          <div><Skeleton className="h-3 w-24" /><Skeleton className="mt-2 h-11 w-full" /></div>
          <div><Skeleton className="h-3 w-28" /><Skeleton className="mt-2 h-11 w-full" /></div>
        </div>
        <Skeleton className="mt-6 h-11 w-full" />
        <span className="sr-only">Checking your reset link…</span>
      </div>
    </AuthLayout>
  );

  if (tokenErr) {
    return (
      <AuthLayout>
        <div data-testid="reset-token-error">
          <h1 className="font-display text-4xl tracking-tight text-center">{tokenErr}</h1>
          <p className="text-stone text-sm mt-3 leading-relaxed text-center">
            Reset links expire after 30 minutes and can only be used once. Request a fresh one below.
          </p>
          <Link to={PUBLIC_ROUTES.FORGOT_PASSWORD} className="mt-8 flex w-full items-center justify-center bg-moss px-6 py-3 text-sm text-linen hover:bg-mossDark" data-testid="reset-request-new">
            Send a new link
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
        <Form {...form}>
        <form onSubmit={form.handleSubmit(submit)} data-testid="reset-form" noValidate>
          <h1 className="font-display text-4xl tracking-tight text-center">Almost there.</h1>
          <p className="text-stone text-sm mt-3 leading-relaxed text-center">
            Resetting the password for <span className="text-ink">{email}</span>.
          </p>
          <div className="mt-8 flex flex-col gap-4">
            <FormInput name="password" label="New password" type="password" autoComplete="new-password" data-testid="reset-password" required />
            <FormInput name="confirmPassword" label="Confirm password" type="password" autoComplete="new-password" data-testid="reset-confirm" required />
          </div>
          <Button
            type="submit"
            variant="dashboardPrimary"
            loading={busy}
            className="mt-6 w-full"
            data-testid="reset-submit"
          >
            Update password & sign in
          </Button>
        </form>
        </Form>
    </AuthLayout>
  );
}
